import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import profileService from '../services/profileService';
import friendService from '../services/friendService';
import squareService from '../services/squareService';
import { safeReturnTo } from '../utils/returnTo';
import { VIBE_MAX } from '../utils/portalPageModel';
import {
  FIRST_SCREEN, INVITE_CAP, LAST_SCREEN, STEP2_BLOCKER, STEP_COUNT, STEP_HEADS, STEP_TITLES, buildOnboardingPayload, contactsHeadline,
  formFromProfile, goLiveLabel, intakeStepFor, markJustJoined, resumeStep, step2Missing, step5Missing, stepNumber,
} from '../utils/onboardingModel';
import { DEFAULT_CITY, getCity, getLocation, setCity as setAppCity } from '../services/userLocation';
import { contactsSource, loadContacts, ContactsDenied } from '../utils/phoneContacts';
import { matchContacts, toE164 } from '../utils/contactMatch';
import { inviteSmsBody, smsHref } from '../utils/smsCompose';
import { fetchInviteLink } from '../components/people/InviteFriendsSheet';
import { StepContacts, StepHereToFind, StepKnowAnyone, StepYourFace } from '../components/onboarding/signupSteps';
import { StepFooter, StepTopBar } from '../components/onboarding/onboardingUi';
import useMediaQuery from '../utils/useMediaQuery';

/**
 * OnboardingPage — `/onboard`, sign-up v3 (design_handoff_signup_v3, Oct 22 2026).
 *
 * People first, profile last. After ENTER (`/register`) a new account walks four steps: KNOW
 * ANYONE HERE? (the YOU strip — your name, required, plus what you do and your handles — and
 * tap the faces you know → friend requests), CONTACTS (the phone's book, hashed on
 * the device and matched → ADD ALL; then pick up to 25 to text your invite link), WHAT ARE YOU HERE
 * TO FIND? (the chips + one line Portal introduces you on), and THE BASICS (an optional photo, your
 * name, one sentence). GO LIVE with a photo lands on the square offering "Make an introduction."
 * (`components/introduction/IntroductionFlow`); GO IN WITHOUT A PHOTO lands there with nothing.
 *
 * Responsive, not a phone in a box: at 900px and up the steps are two panes, the step bar, heading
 * and primary button in a sticky left column and the cards on the right.
 *
 * One page, one form state, one validator. `utils/onboardingModel` holds the rules (what GO LIVE
 * still needs, where a half-finished account resumes, the PUT body) and `utils/portalPageModel`
 * the vocabulary, both shared with the Portal Editor — which is where every edit after this goes.
 * The watermark is still the old 1–8 column: see `resumeStep` / `intakeStepFor`.
 *
 * Nothing from the address book reaches the API except SHA-256 hashes of the numbers
 * (`utils/contactMatch`); the invite text is sent from the person's own phone (`sms:`).
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile, fetchProfile, isLoading: profileLoading } = useUser();
  const desk = useMediaQuery('(min-width: 900px)');
  const fileRef = useRef(null);
  const alive = useRef(true);
  // Set on mount as well as cleared on unmount: StrictMode mounts twice in dev, and a ref
  // cleared by the first unmount would make every async handler bail for the life of the page.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const [dto, setDto] = useState(null);           // the server's copy, for the payload + the avatar
  const [form, setForm] = useState(null);         // what is being filled in
  const [screen, setScreen] = useState(FIRST_SCREEN);
  const [tried, setTried] = useState(false);      // GO LIVE pressed while incomplete
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Screen 2
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(true);
  // Screen 3
  const [contacts, setContacts] = useState({ status: 'idle', source: contactsSource(), received: 0, matches: [], others: [], error: null });
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState('');
  const [typedRows, setTypedRows] = useState([]);
  const [draft, setDraft] = useState('');

  // The town this account is joining: what the ENTER screen showed (the location store), else
  // what the page already says, else the default. It fills a blank location on GO LIVE.
  const city = useMemo(() => getCity() || (dto?.location || '').split(',')[0].trim() || DEFAULT_CITY, [dto?.location]);

  // Hydrate once, and NOT before the profile has had its chance to arrive: `useUser` starts with
  // `profile: null` on every cold load, and hydrating from that would show an empty flow to
  // someone who has already answered half of it — then never correct itself, because this only
  // runs once. `isLoading` covers the auth check and the first fetch.
  useEffect(() => {
    if (form || (!profile && profileLoading)) return;
    const savedName = localStorage.getItem('onboardingDisplayName') || localStorage.getItem('googleSuggestedName') || '';
    if (!profile) { setForm(formFromProfile(null, savedName)); return; }
    setDto(profile);
    setForm(formFromProfile(profile, savedName));
    setScreen(profile.intakeCompletedAt ? FIRST_SCREEN : resumeStep(profile.intakeCurrentStep));
  }, [profile, profileLoading, form]);

  // The faces for KNOW ANYONE HERE?, the town first.
  useEffect(() => {
    let on = true;
    friendService.getSuggestedFriends(12, city).then((r) => {
      if (!on) return;
      setPeople(r.success && Array.isArray(r.data) ? r.data : []);
      setPeopleLoading(false);
    });
    return () => { on = false; };
  }, [city]);

  const set = useCallback((patch) => {
    setForm((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
    setError(null);
  }, []);

  const go = useCallback((next) => {
    setScreen(next);
    setTried(false);
    setError(null);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // ── Saving ────────────────────────────────────────────────────────────────
  const save = async (landingOn, opts = {}) => {
    const body = buildOnboardingPayload(form, dto, landingOn, { city, ...opts });
    let r = dto?.id ? await profileService.updateProfile(body) : await profileService.createProfile(body);
    // createProfile silently drops what its request has no field for, so a brand-new profile is
    // written twice: once to exist, once to hold everything.
    if (r.success && !dto?.id) r = await profileService.updateProfile(body);
    if (!r.success) return r.error || 'Could not save your answers. Try again.';
    if (r.data) setDto(r.data);
    return null;
  };

  /** SKIP / a step with nothing to save: move the watermark only. */
  const bump = (next) => { profileService.updateProfile({ intakeCurrentStep: intakeStepFor(next) }); };

  // ── Screen 2: friend requests for the faces tapped (fire-and-forget, deduped by the API) ──
  // Only ever called once a name is on file: a request with no name on it is the thing the YOU
  // strip exists to prevent. SKIP without a name keeps the taps in the form; GO LIVE sends them.
  const sendTaps = () => {
    const ids = [...form.tapped];
    if (!ids.length) return;
    set({ tapped: [] });
    Promise.allSettled(ids.map((id) => friendService.sendFriendRequest(id)));
  };

  // ── Screen 3: the book → hashes → matches ─────────────────────────────────
  const syncContacts = async () => {
    const source = contactsSource();
    if (source === 'none') { setContacts((c) => ({ ...c, status: 'done', source })); return; }
    setContacts((c) => ({ ...c, status: 'loading', source, error: null }));
    try {
      const { people: book, received, cancelled } = await loadContacts();
      if (!alive.current) return;
      if (source === 'picker' && (cancelled || !received)) { setContacts((c) => ({ ...c, status: 'done', received: 0, matches: [], others: [] })); return; }
      const { checked, matches, others } = await matchContacts(book);
      if (!alive.current) return;
      setContacts({ status: 'done', source, received: received || checked, matches, others, error: null });
    } catch (e) {
      if (!alive.current) return;
      // eslint-disable-next-line no-console
      console.warn('[signup] contacts', e?.name, e?.message);
      setContacts((c) => ({ ...c, status: e instanceof ContactsDenied ? 'denied' : 'error', error: e instanceof ContactsDenied ? null : (e?.message || null) }));
    }
  };

  const addAll = () => {
    const targets = contacts.matches.filter((m) => m.user.relationshipStatus === 'none' || m.user.relationshipStatus === 'request_received');
    set({ addedAll: true });
    Promise.allSettled(targets.map((m) => friendService.sendFriendRequest(m.user.userId)));
  };

  const toggleInvite = (row) => {
    set((s) => {
      const has = s.invites.includes(row.contactId);
      if (has) return { invites: s.invites.filter((id) => id !== row.contactId) };
      if (s.invites.length >= INVITE_CAP) return {};
      return { invites: [...s.invites, row.contactId] };
    });
  };

  const addTyped = () => {
    const e164 = toE164(typed);
    if (!e164) { setError('That doesn’t look like a phone number.'); return; }
    const row = { contactId: `typed:${e164}`, name: e164, number: e164, e164 };
    setTypedRows((rows) => (rows.some((r) => r.e164 === e164) ? rows : [...rows, row]));
    if (!form.invites.includes(row.contactId)) toggleInvite(row);
    setTyped('');
    setError(null);
  };

  const inviteRows = useMemo(() => [...contacts.others, ...typedRows].filter((r) => form?.invites.includes(r.contactId)), [contacts.others, typedRows, form?.invites]);

  const textInvites = async () => {
    const link = await fetchInviteLink();
    if (!link?.url) { setError('Couldn’t get your invite link. Check your connection.'); return false; }
    const body = inviteSmsBody({ name: (form.name || dto?.displayName || '').trim().split(' ')[0] || null, url: link.url });
    try { window.location.href = smsHref(inviteRows.map((r) => r.e164), body); } catch { /* no Messages app; the step still moves on */ }
    return true;
  };

  // ── Screen 4 ──────────────────────────────────────────────────────────────
  const toggleFind = (label) => set((s) => ({ finds: s.finds.includes(label) ? s.finds.filter((f) => f !== label) : [...s.finds, label] }));
  const addDraft = () => {
    const v = draft.trim().replace(/\s+/g, ' ');
    if (!v) return;
    set((s) => ({
      custom: s.custom.some((c) => c.toLowerCase() === v.toLowerCase()) ? s.custom : [...s.custom, v],
      finds: s.finds.some((c) => c.toLowerCase() === v.toLowerCase()) ? s.finds : [...s.finds, v],
    }));
    setDraft('');
  };

  // ── Screen 5: the photo — upload on pick, make it the cover and the profile photo ────────
  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    const up = await profileService.uploadGalleryPhoto(file);
    if (!alive.current) return;
    if (!up.success || !up.data) { setError(up.error || 'That photo could not be uploaded. Try another.'); setUploading(false); return; }
    const id = String(up.data.id);
    const existing = (dto?.galleryPhotos || []).map((p) => String(p.id)).filter((x) => x !== id);
    if (existing.length) await profileService.reorderGalleryPhotos([id, ...existing]);
    const av = await profileService.setAvatarFromGallery(id, 50, 50);
    if (!alive.current) return;
    if (av.success && av.data) setDto(av.data);
    set({ photo: { id, url: (av.success && av.data?.avatarUrl) || up.data.url } });
    setUploading(false);
  };

  const removePhoto = async () => {
    const id = form.photo?.id;
    if (!id) return;
    setUploading(true);
    setError(null);
    const r = await profileService.deleteGalleryPhoto(id);
    if (!alive.current) return;
    setUploading(false);
    if (!r.success) { setError(r.error || 'That photo could not be removed. Try again.'); return; }
    setDto((d) => (d ? { ...d, avatarUrl: null, galleryPhotos: (d.galleryPhotos || []).filter((p) => String(p.id) !== id) } : d));
    set({ photo: null });
  };

  // ── The footer ────────────────────────────────────────────────────────────
  const missing = form ? step5Missing(form) : [];
  const step5Ok = missing.length === 0;
  const nameOk = form ? step2Missing(form).length === 0 : true;

  const finishDestination = () => {
    const next = safeReturnTo(searchParams.get('next'));
    if (next) return next;
    const parked = localStorage.getItem('postOnboardingRedirect');
    if (parked) { localStorage.removeItem('postOnboardingRedirect'); return safeReturnTo(parked); }
    return null;
  };

  const onPrimary = async () => {
    if (saving) return;
    setError(null);
    if (screen === 2) {
      if (!nameOk) { setTried(true); return; }
      // The name (and whatever else the YOU strip holds) is on file BEFORE the requests go out.
      setSaving(true);
      const err = await save(3);
      setSaving(false);
      if (err) { setError(err); return; }
      sendTaps();
      go(3);
      syncContacts();
      return;
    }
    if (screen === 3) {
      if (inviteRows.length) { const ok = await textInvites(); if (!ok) return; }
      bump(4);
      go(4);
      return;
    }
    if (screen === 4) {
      setSaving(true);
      const err = await save(5);
      setSaving(false);
      if (err) { setError(err); return; }
      go(5);
      return;
    }
    // GO LIVE / GO IN WITHOUT A PHOTO
    if (!step5Ok) { setTried(true); return; }
    setSaving(true);
    const err = await save(5, { goLive: true });
    if (err) { setError(err); setSaving(false); return; }
    sendTaps();   // taps kept back by a SKIP without a name — there is one now
    const done = await profileService.markIntakeComplete();
    if (!done.success) { setError(done.error || 'Could not finish setup. Try again.'); setSaving(false); return; }
    markJustJoined();
    localStorage.removeItem('onboardingDisplayName');
    localStorage.removeItem('googleSuggestedName');
    if (!getCity()) setAppCity(city);
    await fetchProfile();
    const loc = getLocation();
    const sq = await squareService.resolve({ lat: loc.lat ?? undefined, lng: loc.lng ?? undefined, label: city });
    const slug = sq.success && sq.data?.slug ? sq.data.slug : null;
    const next = finishDestination();
    // No photo means no introduction, ever surfaced: straight to where they were going.
    if (!form.photo) { navigate(next || (slug ? `/square/${slug}` : '/square'), { replace: true }); return; }
    // With one, the square offers "Make an introduction." on top (`IntroductionFlow`).
    const q = new URLSearchParams({ welcome: '1', city });
    if (next) q.set('next', next);
    navigate(slug ? `/square/${slug}?${q}` : `/square?${q}`, { replace: true });
  };

  const onSkip = () => {
    if (screen === 2) {
      // Past CONTACTS. With a name the strip is saved and the taps go out; without one the taps
      // wait in the form for GO LIVE, where a name is required.
      if (nameOk) { save(4).then((err) => { if (!err) sendTaps(); }); } else { bump(4); }
      go(4);
      return;
    }
    bump(screen + 1);
    go(screen + 1);
  };

  const onBack = () => {
    if (screen > FIRST_SCREEN) go(screen - 1);
    else navigate(-1);
  };

  if (!form) return <div className="pk" style={{ minHeight: '100dvh', background: '#000' }} />;

  const tapped = new Set(form.tapped);
  const invites = new Set(form.invites);
  const n = contacts.matches.length;
  const meta = {
    2: [tapped.size ? `${tapped.size} TAPPED` : '0 TAPPED', tapped.size > 0],
    3: [contacts.status === 'done' && contacts.received ? `${contacts.received} CHECKED` : null, false],
    4: ['1 OF 2', false],
    5: ['2 OF 2', false],
  }[screen];
  const head = STEP_HEADS[screen];
  const h1 = screen === 3 ? contactsHeadline(n, contacts.source) : head[0];
  const primaryLabel = {
    2: tapped.size ? `ADD ${tapped.size} + SYNC CONTACTS →` : 'SYNC CONTACTS →',
    3: inviteRows.length ? `INVITE ${inviteRows.length} BY TEXT →` : 'CONTINUE →',
    4: 'CONTINUE →',
    5: goLiveLabel(form),
  }[screen];
  const skipLabel = screen === 2 ? 'SKIP, I’LL FIND THEM LATER →' : 'SKIP FOR NOW →';
  const footer = (
    <StepFooter
      desk={desk}
      blockers={screen === LAST_SCREEN && tried && !step5Ok ? `Still needed: ${missing.join(', ')}.` : screen === 2 && tried && !nameOk ? STEP2_BLOCKER : null}
      primaryLabel={primaryLabel}
      primaryOff={(screen === LAST_SCREEN && !step5Ok) || (screen === 2 && !nameOk)}
      onPrimary={onPrimary}
      saving={saving}
      helper={screen === LAST_SCREEN ? 'By going in you agree to the Terms and the Privacy Policy.' : null}
      onSkip={screen < LAST_SCREEN ? onSkip : null}
      skipLabel={skipLabel}
    />
  );

  return (
    <div className="pk" data-testid="onboarding" data-step={screen} style={{ minHeight: '100dvh', background: '#000', color: '#fff', overflowX: 'clip' }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        data-testid="photo-file"
        // Copy the file out BEFORE resetting the input: `files` is live, and clearing `value` (what
        // lets the same photo be picked twice in a row) empties the list the handler still holds.
        onChange={(e) => { const picked = Array.from(e.target.files || [])[0]; e.target.value = ''; if (picked) onFile(picked); }}
        style={{ display: 'none' }}
      />
      <div style={{ minHeight: '100dvh', ...(desk
        ? { display: 'grid', gridTemplateColumns: 'minmax(0,400px) minmax(0,1fr)', columnGap: 64, alignContent: 'start', maxWidth: 1180, padding: '0 48px' }
        : { display: 'flex', flexDirection: 'column', maxWidth: 520, padding: '0 18px' }), margin: '0 auto', boxSizing: 'border-box' }}>
        <aside data-testid="onboard-aside" style={desk
          ? { position: 'sticky', top: 0, alignSelf: 'start', height: '100dvh', display: 'flex', flexDirection: 'column', padding: '32px 0', boxSizing: 'border-box' }
          : { display: 'contents' }}>
          <StepTopBar desk={desk} step={stepNumber(screen)} total={STEP_COUNT} title={STEP_TITLES[screen]} meta={meta[0]} metaOn={meta[1]} onBack={onBack} />
          <header style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: desk ? '28px 0 20px' : '16px 0 20px' }}>
            <h1 style={{ margin: 0, fontSize: desk ? 44 : 28, fontWeight: 700, lineHeight: desk ? 1.1 : 1.15, letterSpacing: '-.01em', textWrap: 'balance' }}>{h1}</h1>
            {head[1] && <span style={{ fontSize: 14, lineHeight: 1.6, color: '#8A8A8A' }}>{head[1]}</span>}
          </header>
          {desk && footer}
        </aside>

        <main style={{ flex: desk ? undefined : 1, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, padding: desk ? '32px 0 48px' : 0 }}>
          {screen === 2 && (
            <StepKnowAnyone
              people={people}
              loading={peopleLoading}
              tapped={tapped}
              onToggle={(p) => set((s) => ({ tapped: s.tapped.includes(p.userId) ? s.tapped.filter((id) => id !== p.userId) : [...s.tapped, p.userId] }))}
              you={{ name: form.name, job: form.job, ig: form.ig, tt: form.tt }}
              onYou={set}
              tried={tried}
              city={city}
              cols={desk ? 6 : 4}
            />
          )}
          {screen === 3 && (
            <StepContacts
              contacts={contacts}
              addedAll={form.addedAll}
              onAddAll={addAll}
              invites={invites}
              onToggleInvite={toggleInvite}
              query={query}
              onQuery={setQuery}
              typed={typed}
              onTyped={setTyped}
              onAddTyped={addTyped}
              typedRows={typedRows}
              onRetry={syncContacts}
            />
          )}
          {screen === 4 && <StepHereToFind finds={form.finds} custom={form.custom} onToggleFind={toggleFind} draft={draft} onDraft={setDraft} onAddDraft={addDraft} looking={form.looking} onLooking={(looking) => set({ looking })} />}
          {screen === 5 && <StepYourFace photo={form.photo} uploading={uploading} tried={tried} onPick={() => fileRef.current?.click()} onRemove={form.photo && form.photo.id !== 'avatar' ? removePhoto : null} name={form.name} onName={(name) => set({ name })} vibe={form.vibe} onVibe={(vibe) => set({ vibe: vibe.slice(0, VIBE_MAX) })} />}

          {error && (
            <div role="alert" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 18, background: '#2A0B1C', boxShadow: 'inset 0 0 0 1px #5A1A3E' }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.16em', color: '#FF66C4' }}>DIDN’T SAVE</span>
              <span style={{ fontSize: 12, color: '#FFCCE6' }}>{error}</span>
            </div>
          )}

          {!desk && footer}
        </main>
      </div>
    </div>
  );
}
